import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import theme from '../../styles/theme';
import Button from './Button';
import {
  hasUnsavedChanges,
  getTopDirtyModal,
} from '../../utils/unsavedChangesRegistry';

/**
 * Global guard against losing unsaved form changes.
 *
 * Mounted once (in App) it watches the unsaved-changes registry populated by
 * useUnsavedChanges in each form modal, and:
 *  - shows the "keep editing / discard changes" dialog when a dirty modal
 *    asks to close (event `unsaved-changes:confirm`);
 *  - intercepts Escape while a dirty modal is open;
 *  - blocks browser back/forward (popstate) while a dirty modal is open,
 *    restoring the previous history entry;
 *  - blocks sidebar navigation clicks while a dirty modal is open;
 *  - warns on tab close/refresh (beforeunload) while a dirty modal is open.
 *
 * No interception happens at all while no modal is dirty. Modal backdrops
 * already block the rest of the page on desktop; on mobile the sidebar is
 * above the backdrop, hence the click interception.
 */
const UnsavedChangesProvider = ({ children }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogOpenRef = useRef(false);
  dialogOpenRef.current = dialogOpen;
  // Navigation target captured when a sidebar click was blocked by a dirty
  // modal; executed after the user discards the changes.
  const pendingHrefRef = useRef(null);

  const keepEditing = useCallback(() => {
    pendingHrefRef.current = null;
    setDialogOpen(false);
  }, []);

  const discardAndLeave = useCallback(() => {
    setDialogOpen(false);
    window.dispatchEvent(new Event('unsaved-changes:discarded'));
    const href = pendingHrefRef.current;
    pendingHrefRef.current = null;
    if (href) {
      // Defer until the dirty modal has unmounted and deregistered.
      window.setTimeout(() => {
        window.location.assign(href);
      }, 0);
    }
  }, []);

  // The top dirty modal asks to close -> show the dialog (unless already up).
  useEffect(() => {
    const handler = () => {
      if (!dialogOpenRef.current && hasUnsavedChanges()) {
        setDialogOpen(true);
      }
    };
    window.addEventListener('unsaved-changes:confirm', handler);
    return () => window.removeEventListener('unsaved-changes:confirm', handler);
  }, []);

  // Escape: the topmost dirty modal takes the hit (dialog Escape keeps editing).
  useEffect(() => {
    const handler = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (dialogOpenRef.current) {
        event.stopPropagation();
        keepEditing();
        return;
      }
      if (!hasUnsavedChanges()) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const top = getTopDirtyModal();
      if (top) {
        top.guardClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [keepEditing]);

  // Browser back/forward while dirty: block and restore the previous entry.
  useEffect(() => {
    let suppressRestore = false;
    const handler = (event) => {
      if (!hasUnsavedChanges()) {
        return;
      }
      // Let the popstate triggered by our own restoration pass through.
      if (suppressRestore) {
        suppressRestore = false;
        return;
      }
      event.stopImmediatePropagation();
      setDialogOpen(true);
      suppressRestore = true;
      window.history.back();
    };
    window.addEventListener('popstate', handler, true);
    return () => window.removeEventListener('popstate', handler, true);
  }, []);

  // Sidebar navigation clicks while dirty (mobile sidebar sits above the
  // modal backdrop).
  useEffect(() => {
    const handler = (event) => {
      if (!hasUnsavedChanges() || dialogOpenRef.current) {
        return;
      }
      const target = event.target.closest('a[href]');
      if (!target) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      pendingHrefRef.current = target.getAttribute('href');
      const top = getTopDirtyModal();
      if (top) {
        top.guardClose();
      }
    };
    window.addEventListener('click', handler, true);
    return () => window.removeEventListener('click', handler, true);
  }, []);

  // Tab close/refresh.
  useEffect(() => {
    const handler = (event) => {
      if (!hasUnsavedChanges()) {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  return (
    <>
      {children}
      {dialogOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
          }}
          onClick={keepEditing}
          role="alertdialog"
          aria-modal="true"
        >
          <div
            style={{
              backgroundColor: theme.colors.background.paper,
              borderRadius: theme.borderRadius.xl,
              boxShadow: theme.shadows.xl,
              padding: theme.spacing.xl,
              maxWidth: '420px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.sm,
              }}
            >
              {t('unsavedChanges.title', 'Unsaved changes')}
            </div>

            <p
              style={{
                color: theme.colors.text.secondary,
                fontSize: theme.typography.fontSize.base,
                margin: `0 0 ${theme.spacing.lg}`,
              }}
            >
              {t(
                'unsavedChanges.message',
                'You have unsaved changes. If you leave now they will be lost.'
              )}
            </p>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.spacing.md,
                justifyContent: 'flex-end',
              }}
            >
              <Button variant="secondary" onClick={keepEditing}>
                {t('unsavedChanges.keepEditing', 'Keep editing')}
              </Button>
              <Button variant="danger" onClick={discardAndLeave}>
                {t('unsavedChanges.discardAndLeave', 'Discard changes and leave')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnsavedChangesProvider;
